import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { User, Package, LogOut, AlertCircle, Save, Gift, Copy, Check as CheckIcon } from 'lucide-react';
import { FlagEN, FlagNL } from '../components/ui/Flag';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
    const { user, logout, authHeader, loading } = useAuth();
    const { t, setLang } = useLanguage();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [name, setName] = useState('');
    const [emailLang, setEmailLang] = useState('en');
    const [address, setAddress] = useState({
        first_name: '', last_name: '', street: '', house_number: '',
        postal_code: '', city: '', country: 'NL',
    });
    const [savedMsg, setSavedMsg] = useState(false);
    const [tab, setTab] = useState('orders');

    // Referral state
    const [referral, setReferral] = useState(null);
    const [codeCopied, setCodeCopied] = useState(false);
    const [redeemMsg, setRedeemMsg] = useState('');

    useEffect(() => {
        if (!loading && !user) {
            navigate('/login');
        }
        if (user) {
            setName(user.name || '');
            setEmailLang(user.lang || 'en');
            setAddress({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                street: user.street || '',
                house_number: user.house_number || '',
                postal_code: user.postal_code || '',
                city: user.city || '',
                country: user.country || 'NL',
            });
            axios.get(`${API}/user/orders`, { headers: authHeader() })
                .then(res => setOrders(res.data))
                .catch(console.error);
            axios.get(`${API}/user/referral`, { headers: authHeader() })
                .then(res => setReferral(res.data))
                .catch(console.error);
        }
    }, [user, loading]);

    const saveProfile = async () => {
        try {
            await axios.put(`${API}/auth/profile`, { name, lang: emailLang, ...address }, { headers: authHeader() });
            setLang(emailLang);
            setSavedMsg(true);
            setTimeout(() => setSavedMsg(false), 2000);
        } catch (e) {
            console.error(e);
        }
    };

    const copyReferralCode = async () => {
        if (!referral?.referral_code) return;
        try {
            await navigator.clipboard.writeText(referral.referral_code);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } catch (e) {
            console.error(e);
        }
    };

    const redeemCredit = async () => {
        try {
            const res = await axios.post(`${API}/user/referral/redeem`, {}, { headers: authHeader() });
            setRedeemMsg(res.data.message);
            setReferral(r => ({ ...r, credits_available: res.data.credits_available }));
            setTimeout(() => setRedeemMsg(''), 4000);
        } catch (e) {
            setRedeemMsg(e?.response?.data?.detail || 'Could not redeem');
            setTimeout(() => setRedeemMsg(''), 4000);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div>
            <SEO title="Dashboard | Sleepstack" path="/dashboard" />
            <PreSaleBanner />
            <Navbar />
            <main className="min-h-screen bg-accent" style={{ paddingTop: 'var(--page-offset, 128px)' }}>
                <div className="section-container py-16">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
                        <div>
                            <h1 className="heading-lg text-foreground">{t('dashboard.title')}</h1>
                            <p className="text-muted-foreground text-sm mt-1">
                                {t('dashboard.welcome')}, {user.name || user.email}
                            </p>
                        </div>
                        <button
                            onClick={handleLogout}
                            data-testid="logout-btn"
                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-red-500 transition-colors border border-border rounded-full px-4 py-2"
                        >
                            <LogOut size={15} />
                            {t('dashboard.logout')}
                        </button>
                    </div>

                    {/* Pre-sale phase notice */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="bg-primary/5 border border-primary/20 rounded-2xl p-5 mb-8 flex items-start gap-3"
                        data-testid="presale-notice"
                    >
                        <AlertCircle size={18} className="text-primary shrink-0 mt-0.5" />
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-primary">{t('dashboard.phase_label')}: </span>
                                <span className="bg-secondary text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
                                    {t('dashboard.phase_value')}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {t('dashboard.presale_notice')}
                            </p>
                        </div>
                    </motion.div>

                    {/* Tabs */}
                    <div className="flex gap-1 bg-white border border-border rounded-xl p-1 w-fit mb-8 flex-wrap">
                        {['orders', 'referrals', 'profile'].map(tabKey => (
                            <button
                                key={tabKey}
                                onClick={() => setTab(tabKey)}
                                data-testid={`tab-${tabKey}`}
                                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors capitalize ${
                                    tab === tabKey
                                        ? 'bg-primary text-white'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                {tabKey === 'orders' && (
                                    <span className="flex items-center gap-2"><Package size={14} />{t('dashboard.orders_title')}</span>
                                )}
                                {tabKey === 'referrals' && (
                                    <span className="flex items-center gap-2"><Gift size={14} />{t('dashboard.referral_title')}</span>
                                )}
                                {tabKey === 'profile' && (
                                    <span className="flex items-center gap-2"><User size={14} />{t('dashboard.profile_title')}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Orders tab */}
                    {tab === 'orders' && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="card-base p-6"
                            data-testid="orders-tab"
                        >
                            <h2 className="heading-sm text-foreground mb-6">{t('dashboard.orders_title')}</h2>
                            {orders.length === 0 ? (
                                <div className="text-center py-12">
                                    <Package size={40} className="text-border mx-auto mb-3" />
                                    <p className="text-muted-foreground text-sm">{t('dashboard.no_orders')}</p>
                                    <a href="/checkout" className="btn-secondary text-sm px-6 py-2.5 mt-4 inline-block">
                                        Pre-order now
                                    </a>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {orders.map(order => (
                                        <div
                                            key={order.id}
                                            className="flex items-center justify-between p-4 bg-accent rounded-xl"
                                            data-testid={`order-item-${order.id}`}
                                        >
                                            <div>
                                                <div className="text-sm font-semibold text-foreground">
                                                    #{order.id?.slice(-8).toUpperCase()}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    Sleepstack Sleep Powder · Pre-order
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {new Date(order.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-bold text-foreground">
                                                    €{order.amount?.toFixed(2)}
                                                </div>
                                                <span className="inline-flex items-center bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full mt-1">
                                                    {order.status === 'confirmed' ? 'Confirmed' : order.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Referrals tab */}
                    {tab === 'referrals' && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                            data-testid="referrals-tab"
                        >
                            {/* Code card */}
                            <div className="card-base p-6 md:p-8 border-l-4 border-primary">
                                <h2 className="heading-sm text-foreground mb-1">
                                    {t('dashboard.referral_heading')}
                                </h2>
                                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                                    {t('dashboard.referral_subhead')}
                                </p>
                                <div className="flex flex-col sm:flex-row items-stretch gap-3">
                                    <div className="flex-1 bg-accent rounded-xl px-5 py-4 flex items-center">
                                        <span
                                            className="font-heading text-2xl md:text-3xl font-black text-primary tracking-widest"
                                            data-testid="referral-code"
                                        >
                                            {referral?.referral_code || '————————'}
                                        </span>
                                    </div>
                                    <button
                                        onClick={copyReferralCode}
                                        disabled={!referral?.referral_code}
                                        className="bg-white border border-primary text-primary font-semibold text-sm rounded-xl px-5 py-4 hover:bg-primary hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                                        data-testid="copy-referral-btn"
                                    >
                                        {codeCopied ? <CheckIcon size={15} /> : <Copy size={15} />}
                                        {codeCopied ? t('dashboard.referral_copied') : t('dashboard.referral_copy')}
                                    </button>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="card-base p-5" data-testid="referral-stat-pending">
                                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                                        {t('dashboard.referral_stats_pending')}
                                    </div>
                                    <div className="font-heading text-3xl font-black text-foreground">
                                        {referral?.credits_pending ?? 0}
                                    </div>
                                </div>
                                <div className="card-base p-5 border-2 border-primary" data-testid="referral-stat-available">
                                    <div className="text-xs uppercase tracking-widest font-bold text-primary mb-2">
                                        {t('dashboard.referral_stats_available')}
                                    </div>
                                    <div className="font-heading text-3xl font-black text-primary">
                                        {referral?.credits_available ?? 0}
                                    </div>
                                </div>
                                <div className="card-base p-5" data-testid="referral-stat-total">
                                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                                        {t('dashboard.referral_stats_total')}
                                    </div>
                                    <div className="font-heading text-3xl font-black text-foreground">
                                        {referral?.referrals_count ?? 0}
                                    </div>
                                </div>
                            </div>

                            {/* Redeem */}
                            {(referral?.credits_available ?? 0) > 0 && (
                                <div className="card-base p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                                    <div className="text-sm text-foreground leading-relaxed flex-1">
                                        {t('dashboard.referral_redeem_note')}
                                    </div>
                                    <button
                                        onClick={redeemCredit}
                                        className="btn-secondary text-sm px-5 py-2.5 whitespace-nowrap"
                                        data-testid="redeem-credit-btn"
                                    >
                                        {t('dashboard.referral_redeem')}
                                    </button>
                                </div>
                            )}
                            {redeemMsg && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
                                    {redeemMsg}
                                </div>
                            )}

                            {/* Policy */}
                            <div className="card-base p-6">
                                <h3 className="font-heading text-lg font-black text-foreground mb-4">
                                    {t('dashboard.referral_policy_title')}
                                </h3>
                                <ul className="space-y-3 text-sm text-slate-700 leading-relaxed">
                                    <li className="flex gap-3">
                                        <span className="text-primary font-bold shrink-0">1.</span>
                                        <span>{t('dashboard.referral_policy_1')}</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="text-primary font-bold shrink-0">2.</span>
                                        <span>{t('dashboard.referral_policy_2')}</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="text-primary font-bold shrink-0">3.</span>
                                        <span>{t('dashboard.referral_policy_3')}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Referral history */}
                            {referral?.referrals?.length > 0 && (
                                <div className="card-base p-6">
                                    <h3 className="font-heading text-lg font-black text-foreground mb-4">
                                        Referral history
                                    </h3>
                                    <div className="space-y-2">
                                        {referral.referrals.map(r => (
                                            <div key={r.id} className="flex items-center justify-between p-3 bg-accent rounded-lg text-sm">
                                                <div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {new Date(r.created_at).toLocaleDateString()}
                                                    </div>
                                                    <div className="text-xs text-foreground mt-0.5">
                                                        #{r.id.slice(-8).toUpperCase()}
                                                    </div>
                                                </div>
                                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                                                    r.referral_status === 'earned' ? 'bg-green-100 text-green-700' :
                                                    r.referral_status === 'flagged' ? 'bg-red-100 text-red-700' :
                                                    r.referral_status === 'cancelled' ? 'bg-muted text-muted-foreground' :
                                                    'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                    {r.referral_status}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {referral && referral.referrals?.length === 0 && (
                                <div className="text-center py-8 card-base">
                                    <Gift size={36} className="text-border mx-auto mb-3" />
                                    <p className="text-sm text-muted-foreground">{t('dashboard.referral_empty')}</p>
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Profile tab */}
                    {tab === 'profile' && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="card-base p-6 max-w-md"
                            data-testid="profile-tab"
                        >
                            <h2 className="heading-sm text-foreground mb-6">{t('dashboard.profile_title')}</h2>
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        {t('dashboard.name_label')}
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder="Your name"
                                        data-testid="profile-name-input"
                                        className="w-full h-12 px-4 border border-input rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        {t('dashboard.email_label')}
                                    </label>
                                    <input
                                        type="email"
                                        value={user.email}
                                        disabled
                                        className="w-full h-12 px-4 border border-input rounded-xl text-sm bg-muted text-muted-foreground cursor-not-allowed"
                                    />
                                </div>

                                {/* Email language preference */}
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        {t('dashboard.email_lang_label')}
                                    </label>
                                    <div className="grid grid-cols-2 gap-2" data-testid="profile-lang-toggle">
                                        {[
                                            { v: 'en', label: 'English', Flag: FlagEN },
                                            { v: 'nl', label: 'Nederlands', Flag: FlagNL },
                                        ].map(o => (
                                            <button
                                                key={o.v}
                                                type="button"
                                                onClick={() => setEmailLang(o.v)}
                                                data-testid={`profile-lang-${o.v}`}
                                                className={`h-11 rounded-xl border text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                                                    emailLang === o.v
                                                        ? 'border-primary bg-primary/5 text-primary'
                                                        : 'border-border text-muted-foreground hover:border-primary/50'
                                                }`}
                                            >
                                                <o.Flag size={14} /> {o.label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                        {t('dashboard.email_lang_hint')}
                                    </p>
                                </div>

                                {/* Saved shipping address */}
                                <div className="pt-2">
                                    <div className="text-sm font-semibold text-foreground mb-1">
                                        {t('dashboard.address_label')}
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                                        {t('dashboard.address_hint')}
                                    </p>
                                    <div className="space-y-3" data-testid="profile-address">
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                placeholder={t('checkout.first_name')}
                                                value={address.first_name}
                                                onChange={e => setAddress({...address, first_name: e.target.value})}
                                                className="w-full min-w-0 h-11 px-3 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                data-testid="profile-first-name"
                                            />
                                            <input
                                                type="text"
                                                placeholder={t('checkout.last_name')}
                                                value={address.last_name}
                                                onChange={e => setAddress({...address, last_name: e.target.value})}
                                                className="w-full min-w-0 h-11 px-3 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                data-testid="profile-last-name"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder={t('checkout.street')}
                                            value={address.street}
                                            onChange={e => setAddress({...address, street: e.target.value})}
                                            className="w-full min-w-0 h-11 px-3 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                            data-testid="profile-street"
                                        />
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                placeholder={t('checkout.house_number')}
                                                value={address.house_number}
                                                onChange={e => setAddress({...address, house_number: e.target.value})}
                                                className="w-full min-w-0 h-11 px-3 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                data-testid="profile-house-number"
                                            />
                                            <input
                                                type="text"
                                                placeholder={t('checkout.postal_code')}
                                                value={address.postal_code}
                                                onChange={e => setAddress({...address, postal_code: e.target.value})}
                                                className="w-full min-w-0 h-11 px-3 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                data-testid="profile-postal-code"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder={t('checkout.city')}
                                            value={address.city}
                                            onChange={e => setAddress({...address, city: e.target.value})}
                                            className="w-full min-w-0 h-11 px-3 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                            data-testid="profile-city"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={saveProfile}
                                    data-testid="save-profile-btn"
                                    className="flex items-center gap-2 btn-secondary text-sm py-2.5 px-6"
                                >
                                    <Save size={15} />
                                    {savedMsg ? t('dashboard.saved') : t('dashboard.save')}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
